import React, { useState, useEffect } from "react";
import ExportButtons from "./ExportButtons";

const Customtable = ({
  table_title,
  columns,
  data,
  theadClass,
  divClassName,
  isBordered,
  isPagination = true,
  isGlobalFilter,
  buttonClass,
  buttonName,
  isAddButton,
  isExportButton,
  isCustomPageSize,
  handleUserClick,
  currentPage,
  setCurrentPage,
  loading,
  handleCheckboxChange,
  selectedIds,
}) => {
  const [globalFilter, setGlobalFilter] = useState("");
  const [pageSize, setPageSize] = useState(10);

  // Support controlled or internal pagination
  const internalCurrentPage = currentPage ?? 0;
  const internalSetCurrentPage = setCurrentPage ?? (() => { });

  // Filtered data for global search
  const filteredData = data.filter((row) =>
    columns.some((col) => {
      const key = col.accessorKey;
      const value = row[key];
      return value?.toString().toLowerCase().includes(globalFilter.toLowerCase());
    })
  );

  const pageCount = Math.ceil(filteredData.length / pageSize);
  var currentPageData = filteredData.slice(
    internalCurrentPage * pageSize,
    (internalCurrentPage + 1) * pageSize
  );

  // Reset currentPage when filteredData shrinks and current page becomes invalid
  useEffect(() => {
    if (internalCurrentPage >= pageCount && pageCount > 0) {
      internalSetCurrentPage(0);
    }
  }, [filteredData, pageCount]);

  const visiblePageCount = 4;
  const [pageWindowStart, setPageWindowStart] = useState(0);

  const updatePage = (page) => {
    internalSetCurrentPage(page);

    // Slide the window if clicking near the end or beginning
    if (page >= pageWindowStart + visiblePageCount - 1) {
      setPageWindowStart(page - visiblePageCount + 2); // Shift right
    } else if (page <= pageWindowStart) {
      setPageWindowStart(Math.max(0, page - 1)); // Shift left
    }
  };

  const pageWindowEnd = Math.min(pageWindowStart + visiblePageCount, pageCount);

  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const handleSort = (colKey) => {
    setSortConfig((prev) => {
      if (prev.key === colKey) {
        // toggle asc ↔ desc
        return {
          key: colKey,
          direction: prev.direction === "asc" ? "desc" : "asc",
        };
      }
      // default asc
      return { key: colKey, direction: "asc" };
    });
  };

  const sortedData = React.useMemo(() => {
  let sortable = [...filteredData];
  if (sortConfig.key) {
    sortable.sort((a, b) => {
      const aVal = a[sortConfig.key];
      const bVal = b[sortConfig.key];

      if (aVal === null || aVal === undefined) return 1;
      if (bVal === null || bVal === undefined) return -1;

      // 🔑 Convert to numbers if both are numeric
      const aNum = parseFloat(aVal);
      const bNum = parseFloat(bVal);
      const bothNumeric = !isNaN(aNum) && !isNaN(bNum);

      if (bothNumeric) {
        return sortConfig.direction === "asc"
          ? aNum - bNum
          : bNum - aNum;
      }

      // Otherwise, do string comparison
      return sortConfig.direction === "asc"
        ? aVal.toString().localeCompare(bVal.toString())
        : bVal.toString().localeCompare(aVal.toString());
    });
  }
  return sortable;
}, [filteredData, sortConfig]);

  var currentPageData = sortedData.slice(
    internalCurrentPage * pageSize,
    (internalCurrentPage + 1) * pageSize
  );


  return (
    <>
      <div className=" dt-layout-row dt-layout-row-responsive" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        {isCustomPageSize && (
          <div className="dt-layout-cell dt-start">
            <div className="dt-length">
              <select
                name="dataTable_length"
                aria-controls="dataTable"
                className="dt-input"
                id="dt-length-0"
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setCurrentPage(0);
                }}
              >
                {[10, 25, 50, 100].map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
              <label htmlFor="dt-length-0"> entries per page</label>
            </div>
          </div>
        )}
        {isExportButton ? (
          <div className="dt-layout-cell dt-start">
            <ExportButtons data={data} fileName={table_title} />
          </div>
        ) : !isAddButton ? (
          <div className="dt-layout-cell dt-start"></div>
        ) : null}

        {isAddButton && (
          <div className="dt-layout-cell dt-start">
            <button
              type="button"
              className="btn btn-sm btn-primary"
              onClick={handleUserClick}
            >
              <i className="mdi mdi-plus me-1" /> {buttonName}
            </button>
          </div>
        )}


        {isGlobalFilter && (
          <div className="dt-layout-cell dt-end ">
            <div className="dt-search">
              <label className="d-none" htmlFor="dt-search-0" style={{ marginRight: "0.5rem" }}>
                Search:
              </label>
              <input
                type="search"
                id="dt-search-0"
                className="dt-input"
                placeholder="Search..."
                aria-controls="dataTable"
                value={globalFilter}
                onChange={(e) => setGlobalFilter(e.target.value)}
                style={{ padding: "4px 8px", borderRadius: "3px", border: "1px solid #ccc" }}
              />
            </div>
          </div>
        )}
      </div>

      <div className={divClassName ?? "gridjs-wrapper"}>
        <table
          role="grid"
          className={`gridjs-table table ${isBordered ? "table-bordered" : ""} mb-0`}
          style={{ height: "auto" }}
        >
          <thead className="gridjs-thead">
            <tr className="gridjs-tr">
              {columns.map((col) => (
                <th
                  key={col.accessorKey}
                  onClick={() => handleSort(col.accessorKey)}
                  style={{ cursor: "pointer" }}
                  data-column-id={col.accessorKey}
                  className="gridjs-th gridjs-th-sort"
                  tabIndex="0"
                >
                  <div className="gridjs-th-content">{col.header}</div>
                  <button
                    className={`gridjs-sort ${
                      sortConfig.key === col.accessorKey
                        ? sortConfig.direction === "asc"
                          ? "gridjs-sort-asc"
                          : "gridjs-sort-desc"
                        : "gridjs-sort-neutral"
                    }`}
                    tabIndex="-1"
                  ></button>
                  {col.resizable && <div className="gridjs-th gridjs-resizable"></div>}
                </th>
              ))}
            </tr>
          </thead>

          <tbody className="gridjs-tbody">
            {currentPageData.length ? (
              currentPageData.map((row, rowIndex) => (
                <tr key={rowIndex} className="gridjs-tr">
                  {columns.map((col) => {
                    const cellValue = row[col.accessorKey];

                    if (col.label_condition === "s_no") {
                      return (
                        <td key={col.accessorKey} className="gridjs-td">
                          {internalCurrentPage * pageSize + rowIndex + 1}
                        </td>
                      );
                    }

                    if (col.label_condition === "status") {
                      return (
                        <td key={col.accessorKey} className="gridjs-td">
                          <span className={`badge ${cellValue === 0 ? "badge-yes" : "badge-no"}`}>
                            {cellValue === 0 ? "Active" : "In-Active"}
                          </span>
                        </td>
                      );
                    }

                    if (col.label_condition === "checkbox") {
                      return (
                        <td key={col.accessorKey} className="gridjs-td">
                          <span className={`badge ${cellValue === 1 ? "badge-yes" : "badge-no"}`}>
                            {cellValue === 1 ? "Yes" : "No"}
                          </span>
                        </td>
                      );
                    }

                    if (col.label_condition === "checkbox_list") {
                      return (
                        <td key={col.accessorKey} className="gridjs-td">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            checked={selectedIds?.includes(row.ticket_id_fk)}
                            onChange={(e) => handleCheckboxChange(e, row.ticket_id_fk)}
                          />
                        </td>
                      );
                    }

                    if (col.label_condition === "html") {
                      return (
                        <td
                          key={col.accessorKey}
                          className="gridjs-td"
                          dangerouslySetInnerHTML={{ __html: cellValue }}
                        />
                      );
                    }
                    if (col.label_condition === "array") {
                      return (
                        <td key={col.accessorKey} className="gridjs-td">
                          {(cellValue || []).map((o, i) => (
                            <span
                              key={i}
                              className="badge"
                              style={{
                                background: "#f1f5f9",
                                color: "#0864d3",
                                fontWeight: 600,
                                fontSize: "11px",
                                marginRight: "4px"
                              }}
                            >
                              {o.tobacco_product_name.toUpperCase()}
                              <span style={{ color: "#8b0404", fontWeight: 700 }}>
                                <b>({o.use_qty})</b>
                              </span>
                            </span>
                          ))}
                        </td>
                      );
                    }
                    if (col.label_condition === "icon") {
                      return (
                        <td
                          key={col.accessorKey}
                          className="gridjs-td">
                            <span className="icon-cell" dangerouslySetInnerHTML={{ __html: cellValue }}></span>
                          </td>
                      );
                    }

                    if (col.cell) {
                      return (
                        <td key={col.accessorKey} className="gridjs-td">
                          {col.cell({ row })}
                        </td>
                      );
                    }

                    return (
                      <td key={col.accessorKey} className="gridjs-td">
                        {cellValue}
                      </td>
                    );
                  })}
                </tr>
              ))
            ) : loading ? (
              <tr>
                <td colSpan={columns.length} style={{ textAlign: "center" }}>
                  <div className="loading-dots">
                    Loading<span>.</span>
                    <span>.</span>
                    <span>.</span>
                  </div>
                </td>
              </tr>
            ) : (
              <tr>
                <td colSpan={columns.length} className="text-center py-4">
                  No result found
                </td>
              </tr>
            )}
          </tbody>
        </table>

      </div>

      {/* Pagination Controls */}
      {isPagination && currentPageData.length > 0 && (
        <div className="gridjs-footer">
          <div className="gridjs-pagination">
            
            {/* Summary */}
            <div
              role="status"
              aria-live="polite"
              className="gridjs-summary"
              title={`Page ${internalCurrentPage + 1} of ${pageCount}`}
            >
              Showing{" "}
              <b>{internalCurrentPage * pageSize + 1}</b> to{" "}
              <b>{Math.min((internalCurrentPage + 1) * pageSize, filteredData.length)}</b>{" "}
              of <b>{filteredData.length}</b> results
            </div>

            {/* Pages */}
            <div className="gridjs-pages">
              <button
                tabIndex="0"
                role="button"
                title="Previous"
                aria-label="Previous"
                className={internalCurrentPage === 0 ? "disabled" : ""}
                disabled={internalCurrentPage === 0}
                onClick={() => updatePage(Math.max(0, internalCurrentPage - 1))}
              >
                Previous
              </button>

              {Array.from({ length: pageWindowEnd - pageWindowStart }, (_, i) => {
                const pageIndex = pageWindowStart + i;
                return (
                  <button
                    key={pageIndex}
                    tabIndex="0"
                    role="button"
                    className={pageIndex === internalCurrentPage ? "gridjs-currentPage" : ""}
                    title={`Page ${pageIndex + 1}`}
                    aria-label={`Page ${pageIndex + 1}`}
                    onClick={() => updatePage(pageIndex)}
                  >
                    {pageIndex + 1}
                  </button>
                );
              })}

              <button
                tabIndex="0"
                role="button"
                title="Next"
                aria-label="Next"
                className={internalCurrentPage === pageCount - 1 ? "disabled" : ""}
                disabled={internalCurrentPage === pageCount - 1}
                onClick={() => updatePage(Math.min(internalCurrentPage + 1, pageCount - 1))}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}

    </>
  );
};

export default Customtable;
